import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from './PageShell';
import DialogFooter from './components/DialogFooter';
import Pagination from './components/Pagination';
import ProductSelectionModal from './components/ProductSelectionModal';
import { showToastSuccess, showToastError } from './components/ToastNotifications';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { getAvailableProducts } from './hooks/useMetrcImports';
import {
  getAvailablePackages,
  importPackage as importPackageRecord,
  importPackageCheckArchivedProducts,
  importProductV2,
} from './hooks/usePackages';
import { getEndpointErrorMessage, isSsoReauthRequired, isSsoTemporarilyUnavailable } from './utils/requestErrors';
import { useSsoReauth } from './contexts/SsoReauthContext';

const PACKAGE_IMPORT_PATH = '/packages/import';

const isSsoFailure = (value: unknown) => isSsoReauthRequired(value) || isSsoTemporarilyUnavailable(value);

type SearchCriteria = {
  label: string;
  productName: string;
  batchNumber: string;
  category: string;
  sourceNumber: string;
  testedDate: string;
};

const emptySearchCriteria: SearchCriteria = {
  label: '',
  productName: '',
  batchNumber: '',
  category: '',
  sourceNumber: '',
  testedDate: '',
};

const normalizeSearchValue = (value: string) => {
  const trimmed = String(value || '').trim();
  return trimmed.length >= 3 ? trimmed : '';
};

const hasActiveSearchCriteria = (criteria: SearchCriteria) =>
  Object.values(criteria).some((value) => String(value || '').trim().length > 0);

const areSearchCriteriaEqual = (left: SearchCriteria, right: SearchCriteria) =>
  left.label === right.label &&
  left.productName === right.productName &&
  left.batchNumber === right.batchNumber &&
  left.category === right.category &&
  left.sourceNumber === right.sourceNumber &&
  left.testedDate === right.testedDate;

const hasOwn = (value, property) =>
  Boolean(value) && Object.prototype.hasOwnProperty.call(value, property);

const ARCHIVED_PRODUCT_IMPORT_MESSAGE =
  'This product is archived. Select an active product, or restore this product before importing.';

const PACKAGE_IMPORT_UNCONFIRMED_MESSAGE =
  'The server did not return a valid saved-package ID. The package import could not be confirmed.';

const isSavedImportId = (result: unknown): result is number =>
  typeof result === 'number' && Number.isSafeInteger(result) && result > 0;

const getImportFailureMessage = (result, error, fallback) =>
  (result?.productIsArchived === true ? ARCHIVED_PRODUCT_IMPORT_MESSAGE : null) ||
  result?.respBody?.message ||
  result?.respBody ||
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.body?.message ||
  error?.body ||
  error?.message ||
  fallback;

// Only a resolved, non-error response is an unconfirmed import; message-less errors stay generic.
const getPackageFailureFallback = (result, error) =>
  error || result?.error === true ? 'Unknown error' : PACKAGE_IMPORT_UNCONFIRMED_MESSAGE;

const mergePackageSelectionState = (documents, previousData) => {
  const previousById = new Map(
    (Array.isArray(previousData) ? previousData : []).map((pkg) => [pkg.id, pkg]),
  );

  return (Array.isArray(documents) ? documents : []).map((pkg) => {
    const previousPackage = previousById.get(pkg.id);
    if (!previousPackage?.selected) {
      return pkg;
    }

    return {
      ...pkg,
      selected: true,
      mappedProductId: hasOwn(previousPackage, 'mappedProductId')
        ? previousPackage.mappedProductId
        : pkg.mappedProductId,
      mappedProductName: hasOwn(previousPackage, 'mappedProductName')
        ? previousPackage.mappedProductName
        : pkg.mappedProductName,
    };
  });
};

function estimateTotalForPagedResponse(page, pageSize, documentCount) {
  const safePage = Math.max(0, Number(page) || 0);
  const safePageSize = Math.max(1, Number(pageSize) || 1);
  const safeDocumentCount = Math.max(0, Number(documentCount) || 0);
  const pageStart = safePage * safePageSize;

  if (safeDocumentCount === safePageSize) {
    return pageStart + (safePageSize * 2);
  }

  if (safeDocumentCount > 0) {
    return pageStart + safeDocumentCount;
  }

  return safePage > 0 ? pageStart + 1 : 0;
}

export default function PackageImportPackagesDialog({
  market,
  facility,
  metrcFacilityId,
  organizationId,
  marketName,
  facilityName,
  packages = [],
  onBack,
  onClose,
  onImported = undefined,
  onNext: _onNext,
}) {
  const [search, setSearch] = useState<SearchCriteria>(emptySearchCriteria);
  const [debouncedSearch, setDebouncedSearch] = useState<SearchCriteria>(emptySearchCriteria);
  const debouncedSearchRef = useRef<SearchCriteria>(emptySearchCriteria);
  const [productModalPackage, setProductModalPackage] = useState(null);
  const packagesRequestIdRef = useRef(0);
  const importAttemptInFlightRef = useRef(false);
  const navigate = useNavigate();
  const { handleSsoError } = useSsoReauth();

  // Every selected package fails the same way when no Metrc sign-in works, so they share one prompt.
  // Returns the failures that still need their own message.
  const withoutSsoFailures = <T extends { result?: unknown; error?: unknown }>(failures: T[]): T[] => {
    const ssoFailure = failures.find(({ result, error }) => isSsoFailure(error) || isSsoFailure(result));
    if (!ssoFailure) return failures;
    if (!handleSsoError(isSsoFailure(ssoFailure.error) ? ssoFailure.error : ssoFailure.result, { returnTo: PACKAGE_IMPORT_PATH })) {
      return failures;
    }
    return failures.filter(({ result, error }) => !isSsoFailure(error) && !isSsoFailure(result));
  };

  const showErrorUnlessSso = (error: unknown, fallback: string) => {
    if (handleSsoError(error, { returnTo: PACKAGE_IMPORT_PATH })) return;
    showToastError(getEndpointErrorMessage(error, fallback));
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = {
        label: normalizeSearchValue(search.label),
        productName: normalizeSearchValue(search.productName),
        batchNumber: normalizeSearchValue(search.batchNumber),
        category: normalizeSearchValue(search.category),
        sourceNumber: normalizeSearchValue(search.sourceNumber),
        testedDate: search.testedDate ? String(search.testedDate) : '',
      };
      // Bumping the request id without changing the criteria would discard the in-flight fetch
      // while leaving the effect deps untouched, so nothing would ever refetch.
      if (areSearchCriteriaEqual(debouncedSearchRef.current, nextSearch)) return;
      debouncedSearchRef.current = nextSearch;
      packagesRequestIdRef.current += 1;
      setDebouncedSearch(nextSearch);
      setState((prev) => ({ ...prev, page: 0, pagePending: 0, allSelected: false }));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const [state, setState] = useState({
    loading: false,
    loadingPackages: false,
    loadError: null,
    data: packages,
    page: 0,
    pagePending: 0,
    pageSize: 12,
    total: 0,
    totalNoFilters: 0,
    allSelected: false,
    isPastEnd: false,
    importArchivedProductsPackages: [],
    importArchivedProductsConfirmationPending: false,
  });

  const update = (field, value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const updateSearch = (updater) => {
    setSearch(updater);
  };

  useEffect(() => {
    const requestId = ++packagesRequestIdRef.current;

    async function getResults() {
      setState((prev) => ({ ...prev, loadingPackages: true }));
      const { pagePending: requestedPage, pageSize } = state;

      try {
        let testedDate: string | null = null;
        if (debouncedSearch.testedDate?.length) {
          const parsedDate = new Date(debouncedSearch.testedDate);
          if (Number.isFinite(parsedDate.getTime()) && parsedDate.getFullYear() >= 1970) {
            testedDate = parsedDate.toISOString();
          }
        }

        const available = await getAvailablePackages({
          organizationId,
          marketId: market,
          facilityId: facility,
          includeImported: false,
          label: debouncedSearch.label || null,
          productName: debouncedSearch.productName || null,
          batchNumber: debouncedSearch.batchNumber || null,
          category: debouncedSearch.category || null,
          sourceNumber: debouncedSearch.sourceNumber || null,
          testedDate,
          page: requestedPage,
          pageSize,
        });

        const documents = Array.isArray(available?.documents)
          ? available.documents
          : Array.isArray(available)
            ? available
            : [];
        const rawTotal = available?.total;
        const parsedTotal = Number(rawTotal);
        const hasKnownTotal = rawTotal !== undefined && rawTotal !== null && rawTotal !== '' && Number.isFinite(parsedTotal);
        const estimatedTotal = estimateTotalForPagedResponse(
          requestedPage,
          pageSize,
          documents.length,
        );
        const total = hasKnownTotal
          ? parsedTotal
          : estimatedTotal;

        const isPastEnd = documents.length < pageSize;

        if (requestId !== packagesRequestIdRef.current) return;

        setState((prev) => ({
          ...prev,
          page: requestedPage,
          pagePending: requestedPage,
          data: mergePackageSelectionState(documents, prev.data),
          isPastEnd,
          total,
          totalNoFilters: hasActiveSearchCriteria(debouncedSearch) ? prev.totalNoFilters : total,
        }));
      } catch (err) {
        if (requestId !== packagesRequestIdRef.current) return;
        showErrorUnlessSso(err, 'Could not retrieve packages');
      } finally {
        if (requestId === packagesRequestIdRef.current) {
          setState((prev) => ({ ...prev, loadingPackages: false }));
        }
      }
    }

    getResults();
  }, [debouncedSearch, facility, market, organizationId, state.pagePending, state.pageSize]);

  const selected = useMemo(() => {
    return (state.data || []).filter((p) => p.selected);
  }, [state.data]);

  const toggle = (id) => {
    setState((prev) => ({
      ...prev,
      data: (prev.data || []).map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)),
    }));
  };

  const setAll = () => {
    setState((prev) => ({
      ...prev,
      allSelected: !prev.allSelected,
      data: (prev.data || []).map((p) => ({
        ...p,
        selected: !prev.allSelected && !p.importHistoryExists,
      })),
    }));
  };

  const notifyPackagesImported = (packageIds, showToast = true) => {
    if (!packageIds.length) {
      return;
    }

    setState((prev) => ({
      ...prev,
      data: prev.data?.filter((item) => !packageIds.some((packageId) => packageId === item.id)),
      loading: false,
    }));

    if (showToast) {
      showToastSuccess('Packages successfully imported');
    }

    if (onImported) {
      onImported();
    } else {
      navigate(-1);
    }
  };

  const checkImportPackagesHasArchivedProducts = async () => {
    const payload = {
      organizationId,
      facilityId: facility,
      packages: [],
    };

    for (const pkg of selected) {
      if (pkg.mappedProductId) {
        payload.packages.push({
          metrcPackageId: pkg.id,
          productId: pkg.mappedProductId,
        });
      }
    }

    if (!payload.packages.length) {
      return false;
    }

    const results = await importPackageCheckArchivedProducts(payload);
    if (results?.error) {
      throw results;
    }

    // The preflight must account for every mapped package before any import begins.
    if (!Array.isArray(results) || payload.packages.some((pkg) => !results.some((result) =>
      result?.metrcPackageId === pkg.metrcPackageId &&
      result?.productId === pkg.productId &&
      typeof result?.isProductActive === 'boolean',
    ))) {
      throw new Error('Could not verify product availability. Please try again before importing.');
    }

    const packagesWithArchivedProduct = results
      .filter((result) => result?.isProductActive === false)
      .map((result) => ({
        ...result,
        packageLabel: selected.find((pkg) => pkg?.id === result.metrcPackageId)?.label || '',
      }));

    update('importArchivedProductsPackages', packagesWithArchivedProduct);
    const hasArchivedProducts = packagesWithArchivedProduct.length > 0;
    update('importArchivedProductsConfirmationPending', hasArchivedProducts);
    return hasArchivedProducts;
  };

  const importPackages = async () => {
    const mappedPackages = selected.filter((p) => p.mappedProductId);
    const unMappedPackages = selected.filter((p) => !p.mappedProductId);
    setState((prev) => ({ ...prev, loading: true }));

    const importMappedPackage = async (pkg) => {
      try {
        const result = await importPackageRecord({
          organizationId,
          facilityId: facility,
          productId: pkg.mappedProductId,
          metrcPackageId: pkg.id,
        });
        return {
          pkg,
          result,
          error: null,
          succeeded: isSavedImportId(result),
        };
      } catch (error) {
        return {
          pkg,
          result: null,
          error,
          succeeded: false,
        };
      }
    };

    const mappedOutcomes = await Promise.all(
      mappedPackages.map((pkg) => importMappedPackage(pkg)),
    );

    const failedMappedOutcomes = mappedOutcomes.filter(({ succeeded }) => !succeeded);
    const successfulMappedIds = mappedOutcomes
      .filter(({ succeeded }) => succeeded)
      .map(({ pkg }) => pkg.id);

    for (const { result, error, pkg } of withoutSsoFailures(failedMappedOutcomes)) {
      const message = getImportFailureMessage(result, error, getPackageFailureFallback(result, error));
      showToastError(`Failed to import package ${pkg.label}: ${message}`);
    }

    if (failedMappedOutcomes.length > 0) {
      setState((prev) => ({
        ...prev,
        data: prev.data?.filter((item) => !successfulMappedIds.includes(item.id)),
        loading: false,
      }));
      if (successfulMappedIds.length > 0) {
        showToastSuccess(
          `${successfulMappedIds.length} package(s) imported; failed package(s) remain selected for retry.`,
        );
      }
      return;
    }

    if (!unMappedPackages.length) {
      if (successfulMappedIds.length > 0) {
        notifyPackagesImported(successfulMappedIds);
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    const finalizeUnmappedOutcomes = (successfulIds, failedCount) => {
      if (failedCount > 0) {
        setState((prev) => ({
          ...prev,
          data: prev.data?.filter((item) => !successfulIds.includes(item.id)),
          loading: false,
        }));
        if (successfulIds.length > 0) {
          showToastSuccess(
            `${successfulIds.length} package(s) imported; failed package(s) remain selected for retry.`,
          );
        }
        return;
      }

      if (successfulIds.length > 0) {
        notifyPackagesImported(successfulIds);
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    let availableProducts;
    try {
      availableProducts = await getAvailableProducts({
        organizationId,
        marketId: market,
        metrcFacilityId,
        page: 0,
        pageSize: 1000,
        metrcPackageIds: unMappedPackages.map((pkg) => pkg.id),
      });
    } catch (error) {
      showErrorUnlessSso(error, 'Error retrieving available products');
      finalizeUnmappedOutcomes(successfulMappedIds, unMappedPackages.length);
      return;
    }

    const handleUnmappedError = (message) => {
      if (successfulMappedIds.length > 0) {
        notifyPackagesImported(successfulMappedIds, false);
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }

      showToastError(message);
    };

    const availableProductDocuments = Array.isArray(availableProducts?.documents)
      ? availableProducts.documents
      : Array.isArray(availableProducts)
        ? availableProducts
        : [];

    const matchingProducts = availableProductDocuments.filter((product) =>
      unMappedPackages.some((pkg) => pkg.itemId === product.id),
    );
    const unmappedNoProduct = unMappedPackages.filter(
      (pkg) => !matchingProducts.some((product) => product.id === pkg.itemId),
    );

    let unmappedNoProductOutcomes = [];
    if (unmappedNoProduct.length > 0) {
      unmappedNoProductOutcomes = await Promise.all(
        unmappedNoProduct.map(async (pkg) => {
          try {
            const productId = await importProductV2({
              organizationId,
              facilityId: facility,
              metrcProductId: pkg.itemId,
              metrcPackageId: pkg.id,
            });

            if (!isSavedImportId(productId)) {
              return {
                pkg,
                productId: null,
                result: productId,
                error: new Error('The product import did not return a product ID.'),
                succeeded: false,
              };
            }

            const result = await importPackageRecord({
              organizationId,
              facilityId: facility,
              productId,
              metrcPackageId: pkg.id,
            });

            return {
              pkg,
              productId,
              result,
              error: null,
              succeeded: isSavedImportId(result),
            };
          } catch (error) {
            return { pkg, productId: null, result: null, error, succeeded: false };
          }
        }),
      );

      const failures = unmappedNoProductOutcomes.filter(({ succeeded }) => !succeeded);

      for (const { pkg, result, error } of withoutSsoFailures(failures)) {
        showToastError(
          `Failed to import package ${pkg.label}: ${getImportFailureMessage(
            result,
            error,
            getPackageFailureFallback(result, error),
          )}`,
        );
      }
    }

    const successfulUnmappedNoProductIds = unmappedNoProductOutcomes
        .filter(({ succeeded }) => succeeded)
        .map(({ pkg }) => pkg.id);
    const failedUnmappedNoProductCount = unmappedNoProductOutcomes.length
      - successfulUnmappedNoProductIds.length;

    if (!matchingProducts.length) {
      const successfulIds = [...successfulMappedIds, ...successfulUnmappedNoProductIds];
      finalizeUnmappedOutcomes(successfulIds, failedUnmappedNoProductCount);
      return;
    }

    try {
      const productOutcomes = await Promise.all(matchingProducts.map(async (product) => {
        const pkg = unMappedPackages.find((item) => item.itemId === product.id);
        try {
          const productId = await importProductV2({
            organizationId,
            facilityId: facility,
            metrcProductId: product.id,
            metrcPackageId: pkg?.id,
          });
          if (!isSavedImportId(productId)) {
            return {
              pkg,
              productId: null,
              result: productId,
              error: new Error('The product import did not return a product ID.'),
              succeeded: false,
            };
          }
          return {
            pkg,
            productId,
            result: null,
            error: null,
            succeeded: true,
          };
        } catch (error) {
          return { pkg, productId: null, result: null, error, succeeded: false };
        }
      }));

      // Resolve/import a product once, but track every selected package separately.
      // Several packages may refer to the same Metrc item.
      const productOutcomeByItemId = new Map(
        productOutcomes.map((outcome) => [outcome.pkg?.itemId, outcome]),
      );
      const matchingPackages = unMappedPackages.filter((pkg) => productOutcomeByItemId.has(pkg.itemId));
      const importPackageRequests = matchingPackages.map(async (pkg) => {
        const productOutcome = { ...productOutcomeByItemId.get(pkg.itemId), pkg };
        if (!productOutcome.succeeded) return productOutcome;

        try {
          const result = await importPackageRecord({
            organizationId,
            facilityId: facility,
            productId: productOutcome.productId,
            metrcPackageId: productOutcome.pkg?.id,
          });
          return {
            ...productOutcome,
            result,
            error: null,
            succeeded: isSavedImportId(result),
          };
        } catch (error) {
          return { ...productOutcome, result: null, error, succeeded: false };
        }
      });

      const unmappedOutcomes = await Promise.all(importPackageRequests);
      const failedUnmappedOutcomes = unmappedOutcomes.filter(({ succeeded }) => !succeeded);
      for (const { pkg, result, error } of withoutSsoFailures(failedUnmappedOutcomes)) {
        showToastError(
          `Failed to import package ${pkg?.label || ''}: ${getImportFailureMessage(
            result,
            error,
            getPackageFailureFallback(result, error),
          )}`,
        );
      }

      const successfulMatchingIds = unmappedOutcomes
        .filter(({ succeeded }) => succeeded)
        .map(({ pkg }) => pkg?.id)
        .filter((id) => id !== null && id !== undefined);
      const successfulIds = [
        ...successfulMappedIds,
        ...successfulUnmappedNoProductIds,
        ...successfulMatchingIds,
      ];
      finalizeUnmappedOutcomes(
        successfulIds,
        failedUnmappedNoProductCount + failedUnmappedOutcomes.length,
      );
    } catch {
      handleUnmappedError('Error trying to import unmapped packages(s)');
    }
  };

  const tryStartImportingPackages = async () => {
    if (importAttemptInFlightRef.current) return;
    importAttemptInFlightRef.current = true;
    setState((prev) => ({ ...prev, loading: true }));
    let importStarted = false;

    try {
      const hasArchivedProducts = await checkImportPackagesHasArchivedProducts();
      if (hasArchivedProducts) {
        return;
      }
      importStarted = true;
      await importPackages();
    } catch (err) {
      if (handleSsoError(err, { returnTo: PACKAGE_IMPORT_PATH })) return;
      const errorMessage = getEndpointErrorMessage(err, 'Could not import packages / product mappings');
      showToastError(errorMessage);
      if (importStarted) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    } finally {
      importAttemptInFlightRef.current = false;
      if (!importStarted) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  const assignProduct = (pack, item) => {
    setState((prev) => ({
      ...prev,
      data: prev.data.map((pkg) =>
        pkg.id === pack.id
          ? {
              ...pkg,
              mappedProductId: item?.id ?? item?.productId ?? null,
              mappedProductName: item?.name || item?.productName || null,
            }
          : pkg,
      ),
    }));
  };

  const unAssignProduct = (pack) => {
    setState((prev) => ({
      ...prev,
      data: prev.data.map((pkg) =>
        pkg.id === pack.id
          ? { ...pkg, mappedProductId: null, mappedProductName: null }
          : pkg,
      ),
    }));
  };

  const footer = (
    <DialogFooter withTopDivider dividerStyle={{ marginTop: 12, marginBottom: 10 }}>
      <button className="button secondary" onClick={onBack} data-testid="package-import-packages-back">
        Back
      </button>
      <button
        className="button"
        data-testid="package-import-packages-next"
        onClick={() => {
          tryStartImportingPackages();
        }}
        disabled={(selected?.length || 0) === 0 || state.loading}
      >
        {state.loading ? 'Importing...' : 'Next'}
      </button>
    </DialogFooter>
  );

  return (
    <PageShell
      contextLabel="Packages"
      title="Select Packages"
      onClose={onClose}
      framed={false}
      testId="package-import-packages"
      preview={
        <div className="card" style={{ padding: 16 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
            Preview
          </div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Packages to Import</div>
          <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6, marginBottom: 10 }}>
            <div>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span>{' '}
              <span data-testid="package-import-packages-preview-market">{marketName || '—'}</span>
            </div>
            <div>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Facility:</span>{' '}
              <span data-testid="package-import-packages-preview-facility">{facilityName || '—'}</span>
            </div>
            <div>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Total:</span>{' '}
              <span data-testid="package-import-packages-preview-total">{state.totalNoFilters}</span>
            </div>
            <div>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Filtered:</span>{' '}
              <span data-testid="package-import-packages-preview-filtered">{state.total}</span>
            </div>
            <div>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Selected:</span>{' '}
              <span data-testid="package-import-packages-preview-selected">{selected?.length || 0}</span>
            </div>
          </div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
            Selected Package
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {(selected?.length || 0) === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>
                None selected yet.
              </div>
            ) : (
              Array.from(selected)
                .slice(0, 8)
                .map((pkg) => (
                  <div key={pkg.id} className="card" style={{ padding: 10 }} data-testid={`package-import-packages-preview-package-${String(pkg.id || '').toLowerCase()}`}>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{`${pkg.itemName}   ${pkg.label}`}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {state.data.find((item) => item.id === pkg.id)?.mappedProductName || 'Unassigned'}
                    </div>
                  </div>
                ))
            )}
            {(selected?.length || 0) > 8 && (
              <div className="muted" style={{ fontSize: 12 }}>
                +{(selected?.length || 0) - 8} more
              </div>
            )}
          </div>
        </div>
      }
      footer={footer}
    >
      <FullScreenConfirmOverlay
        show={state.importArchivedProductsConfirmationPending}
        title="Archived product cannot be imported"
        message={`Package${state.importArchivedProductsPackages.length === 1 ? '' : 's'} ${state.importArchivedProductsPackages.map((pkg) => pkg?.packageLabel || '').join(', ')} ${state.importArchivedProductsPackages.length === 1 ? 'is' : 'are'} mapped to an archived product. Select an active product using Change, or ask an authorized administrator to restore the archived product before importing. No packages have been imported.`}
        cancelLabel="Cancel"
        confirmLabel="Back to selection"
        busy={state.loading}
        onCancel={() => {
          update('importArchivedProductsConfirmationPending', false);
          update('importArchivedProductsPackages', []);
        }}
        onConfirm={() => {
          update('importArchivedProductsConfirmationPending', false);
          update('importArchivedProductsPackages', []);
        }}
        testId="import-packages-archived-products-overlay"
      />
      <div className="form-grid">
        <div className="field full-span">
          <label className="label">Package ID</label>
          <input
            className="input"
            placeholder="Search by ID, minimum 3 characters"
            value={search.label}
            data-testid="package-import-packages-search-id"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, label: e.target.value }));
            }}
          />
        </div>
        <div className="field">
          <label className="label">Product Name</label>
          <input
            className="input"
            placeholder="Search by product, minimum 3 characters"
            value={search.productName}
            data-testid="package-import-packages-search-product"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, productName: e.target.value }));
            }}
          />
        </div>
        <div className="field">
          <label className="label">Batch Number</label>
          <input
            className="input"
            placeholder="Search by batch, minimum 3 characters"
            value={search.batchNumber}
            data-testid="package-import-packages-search-batch"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, batchNumber: e.target.value }));
            }}
          />
        </div>
        <div className="field">
          <label className="label">Category</label>
          <input
            className="input"
            placeholder="Search by category, minimum 3 characters"
            value={search.category}
            data-testid="package-import-packages-search-category"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, category: e.target.value }));
            }}
          />
        </div>
        <div className="field">
          <label className="label">Source Number</label>
          <input
            className="input"
            placeholder="Search by source #, minimum 3 characters"
            value={search.sourceNumber}
            data-testid="package-import-packages-search-source-number"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, sourceNumber: e.target.value }));
            }}
          />
        </div>
        <div className="field">
          <label className="label">Tested Date</label>
          <input
            type="date"
            className="input"
            value={search.testedDate}
            data-testid="package-import-packages-search-tested-date"
            onChange={(e) => {
              updateSearch((prev) => ({ ...prev, testedDate: e.target.value }));
            }}
          />
        </div>
        <div className="field full-span">
          <div
            data-testid="package-import-packages-results"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div>
              <label className="label" style={{ marginBottom: 0 }}>
                Packages
              </label>
              <div className="muted" style={{ fontSize: 12 }}>
                Showing up to {state.pageSize} at a time
              </div>
            </div>
          </div>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: 8,
              minHeight: 260,
              maxHeight: 260,
              overflowY: 'auto',
            }}
          >
            {state.data?.length === 0 && (
              <div className="muted" style={{ padding: 8 }} data-testid="package-import-packages-results-empty">
                No packages match this filter.
              </div>
            )}
            {state.total > 0 && (
              <div>
                <input
                  type="checkbox"
                  checked={state.allSelected}
                  onChange={setAll}
                  aria-label="Select all products"
                  data-testid="package-import-packages-select-all"
                />
                <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
                  Select all
                </span>
              </div>
            )}
            {state.data?.map((pkg) => (
              <div
                key={pkg.id}
                data-testid={`package-import-packages-row-${String(pkg.id || '').toLowerCase()}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px' }}
              >
                <input
                  type="checkbox"
                  value={pkg.label || ''}
                  checked={selected?.some((item) => item.id == pkg.id) || false}
                  disabled={pkg.importHistoryExists}
                  title={pkg.importHistoryExists ? 'This package was already imported' : ''}
                  onChange={() => toggle(pkg.id)}
                  data-testid={`package-import-package-check-${String(pkg.id || '').toLowerCase()}`}
                />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(200px,1fr) minmax(300px,1fr) 0.05fr 1fr',
                    lineHeight: '40px',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{`${pkg.itemName}`}</div>
                  <div style={{ fontWeight: 700 }}>{`${pkg.label}`}</div>
                  <div></div>
                  <div>
                    {selected?.some((item) => item.id == pkg.id) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>
                            {pkg.mappedProductName || <span className="muted">No product</span>}
                          </span>
                          <button
                            className="button secondary"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => setProductModalPackage(pkg)}
                            data-testid={`package-import-packages-select-product-${String(pkg.id || '').toLowerCase()}`}
                          >
                            {pkg.mappedProductId ? 'Change' : 'Select Product'}
                          </button>
                          {pkg.mappedProductId && (
                            <button
                              className="button secondary"
                              style={{ fontSize: 12, padding: '4px 10px' }}
                              onClick={() => unAssignProduct(pkg)}
                              data-testid={`package-import-packages-clear-product-${String(pkg.id || '').toLowerCase()}`}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            ))}
          </div>
          <Pagination
            page={state.page}
            totalPages={Math.max(1, Math.ceil((state.total || 0) / state.pageSize))}
            hideWhenSingle={false}
            prevDisabled={state.loadingPackages || state.loading || state.pagePending <= 0}
            nextDisabled={state.loadingPackages || state.loading || state.isPastEnd}
            onPrev={() => {
              setState((prev) => ({
                ...prev,
                pagePending: Math.max(0, prev.page - 1),
                allSelected: false,
              }));
            }}
            onNext={() => {
              setState((prev) => ({
                ...prev,
                pagePending: prev.page + 1,
                allSelected: false,
              }));
            }}
            prevLabel="◀"
            nextLabel="▶"
            format="of"
            testId="package-import-packages-pagination"
          />
        </div>
      </div>
      <ProductSelectionModal
        show={!!productModalPackage}
        organizationId={organizationId}
        initialMarketId={market}
        onCancel={() => setProductModalPackage(null)}
        onSelect={(product) => {
          if (productModalPackage) assignProduct(productModalPackage, product);
          setProductModalPackage(null);
        }}
      />
    </PageShell>
  );
}
